import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aiBetTrashTalk, createBet, getSchedule, type BetSide } from '../api';
import type { EnrichedMatch } from '../types';
import { useAuth } from '../auth';
import { rigaDate, rigaTime } from '../format';
import { toast } from '../toast';
import { haptic } from '../haptic';

const PLAYERS = ['Armands', 'Edgars', 'Valters', 'Emīls', 'Einārs', 'Raivis', 'Edijs', 'Jānis'];
const STAKE_PRESETS = [1, 5, 10, 20, 50];

interface Props {
  open: boolean;
  onClose: () => void;
  initialMatch?: EnrichedMatch | null;
  initialOpponent?: string | null;
  initialDescription?: string;
}

type ExpirySlot = '1h' | '6h' | '24h' | '7d' | 'custom' | 'never';

export default function ProposeBetSheet({ open, onClose, initialMatch, initialOpponent, initialDescription }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [audience, setAudience] = useState<'direct' | 'marketplace'>('direct');
  const [opponent, setOpponent] = useState<string>(initialOpponent ?? '');
  const [includeMatch, setIncludeMatch] = useState<boolean>(!!initialMatch);
  const [matchId, setMatchId] = useState<string | null>(initialMatch?.id ?? null);
  const [description, setDescription] = useState<string>(initialDescription ?? '');
  const [side, setSide] = useState<BetSide>('yes');
  const [oddsMode, setOddsMode] = useState<'even' | 'variable'>('even');
  const [evenStake, setEvenStake] = useState<number>(5);
  const [proposerStake, setProposerStake] = useState<number>(10);
  const [opponentStake, setOpponentStake] = useState<number>(10);
  const [expiry, setExpiry] = useState<ExpirySlot>('never');
  const [customExpiry, setCustomExpiry] = useState<string>('');     // datetime-local value
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const { data: schedule } = useQuery({ queryKey: ['schedule'], queryFn: getSchedule, enabled: open });

  useEffect(() => {
    if (!open) return;
    setError(null);
    setAudience('direct');
    setOpponent(initialOpponent ?? '');
    setIncludeMatch(!!initialMatch);
    setMatchId(initialMatch?.id ?? null);
    setDescription(initialDescription ?? '');
    setSide('yes');
    setOddsMode('even');
    setEvenStake(5);
    setProposerStake(10);
    setOpponentStake(10);
    setExpiry('never');
    setCustomExpiry('');
    setMessage('');
  }, [open, initialMatch, initialOpponent, initialDescription]);

  const upcoming: EnrichedMatch[] = useMemo(() => {
    const all = schedule?.matches ?? [];
    return all.filter((m) => m.status !== 'post');
  }, [schedule]);

  const selectedMatch = useMemo(
    () => upcoming.find((m) => m.id === matchId) ?? initialMatch ?? null,
    [upcoming, matchId, initialMatch],
  );

  const stakes = oddsMode === 'even'
    ? { proposer: evenStake, opponent: evenStake }
    : { proposer: proposerStake, opponent: opponentStake };

  const oddsRatio = stakes.proposer > 0 ? (stakes.opponent / stakes.proposer) : 0;

  const mut = useMutation({
    mutationFn: createBet,
    onSuccess: async (_data, body) => {
      haptic.success();
      toast.success(`Bet sent to ${body.opponent}.`);
      await qc.invalidateQueries({ queryKey: ['bets'] });
      await qc.invalidateQueries({ queryKey: ['public-bets'] });
      onClose();
    },
    onError: (e: Error) => { haptic.error(); setError(e.message); toast.error(e.message); },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!user) { setError('Sign in to place bets.'); return; }
    if (audience === 'direct') {
      if (!opponent || opponent === user) { setError('Pick a different opponent.'); return; }
    }
    const desc = description.trim();
    if (!desc) { setError('Describe the bet.'); return; }
    if (stakes.proposer < 1 || stakes.opponent < 1) { setError('Stakes must be at least €1.'); return; }
    const isMarketplace = audience === 'marketplace';
    const tiedMatchId = includeMatch ? (selectedMatch?.id ?? null) : null;
    // For untied marketplace bets, optionally encode an expiry offset; tied ones expire at kickoff server-side.
    let expiresAt: string | null = null;
    if (isMarketplace && !tiedMatchId && expiry !== 'never') {
      if (expiry === 'custom') {
        if (!customExpiry) { setError('Pick a custom expiry date/time.'); return; }
        const dt = new Date(customExpiry);
        if (Number.isNaN(dt.getTime())) { setError('Invalid expiry date.'); return; }
        if (dt.getTime() <= Date.now()) { setError('Expiry must be in the future.'); return; }
        expiresAt = dt.toISOString();
      } else {
        const offsets: Record<Exclude<ExpirySlot, 'custom' | 'never'>, number> = {
          '1h': 3600_000, '6h': 21_600_000, '24h': 86_400_000, '7d': 604_800_000,
        };
        expiresAt = new Date(Date.now() + offsets[expiry]).toISOString();
      }
    }
    mut.mutate({
      opponent: isMarketplace ? undefined : opponent,
      match_id: tiedMatchId,
      description: desc,
      proposer_side: side,
      proposer_stake: stakes.proposer,
      opponent_stake: stakes.opponent,
      message: message.trim() || undefined,
      is_marketplace: isMarketplace,
      expires_at: expiresAt,
    });
  }

  if (!open) return null;
  const candidates = PLAYERS.filter((p) => p !== user);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center sm:justify-center" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="card w-full sm:w-[28rem] p-5 rounded-t-3xl sm:rounded-2xl space-y-3 max-h-[92vh] overflow-auto ring-1 ring-pitch-500/20"
        style={{ paddingBottom: 'max(1.25rem, calc(env(safe-area-inset-bottom) + 1rem))' }}
        data-allow-select
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">Propose a bet</h2>
          <button type="button" onClick={onClose} className="text-dim text-xl leading-none px-2">×</button>
        </div>
        <p className="text-xs text-dim">
          Freeform bet. Once it's over, both sides confirm the outcome.
        </p>

        <Field label="Audience">
          <div className="flex gap-2 mb-2">
            <RadioBtn label="Direct (pick opponent)" active={audience === 'direct'} onClick={() => setAudience('direct')} />
            <RadioBtn label="Marketplace (open)" active={audience === 'marketplace'} onClick={() => setAudience('marketplace')} />
          </div>
          {audience === 'direct' ? (
            <select
              value={opponent} onChange={(e) => setOpponent(e.target.value)}
              className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk"
            >
              <option value="">Pick a player…</option>
              {candidates.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          ) : (
            <p className="text-[11px] text-dim">
              First league member to claim becomes the opponent. {includeMatch
                ? 'Tied to a match — auto-cancels at kickoff.'
                : expiry === 'never'
                  ? <span className="text-amber-400">No expiry — you must cancel it yourself if it goes stale.</span>
                  : expiry === 'custom'
                    ? customExpiry
                      ? `Auto-cancels at ${new Date(customExpiry).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}.`
                      : 'Pick a date/time below.'
                    : `Auto-cancels in ${expiryLabel(expiry)}.`}
            </p>
          )}
        </Field>

        <Field label="What's the bet?">
          <textarea
            value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Mexico won't make it out of the group stage"
            className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk min-h-[5rem] resize-y"
          />
        </Field>

        <Field label="Your side on this statement">
          <div className="grid grid-cols-2 gap-1.5">
            <SideBtn label="YES — I agree" sub="The statement holds true" active={side === 'yes'} onClick={() => setSide('yes')} />
            <SideBtn label="NO — I disagree" sub="The statement is false" active={side === 'no'} onClick={() => setSide('no')} />
          </div>
          <p className="text-[10px] text-dim mt-1.5">
            {opponent ? `${opponent} automatically takes the opposite side.` : "Your opponent takes the opposite side."}
          </p>
        </Field>

        <Field label="Match context">
          <div className="flex gap-2 mb-2">
            <RadioBtn label="No context" active={!includeMatch} onClick={() => setIncludeMatch(false)} />
            <RadioBtn label="Tie to a match" active={includeMatch} onClick={() => setIncludeMatch(true)} />
          </div>
          {includeMatch && (
            initialMatch ? (
              <MatchPreview m={initialMatch} />
            ) : (
              <select
                value={matchId ?? ''} onChange={(e) => setMatchId(e.target.value || null)}
                className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk"
              >
                <option value="">— pick a match —</option>
                {upcoming.slice(0, 60).map((m) => (
                  <option key={m.id} value={m.id}>
                    {rigaDate(m.utc_kickoff)} {rigaTime(m.utc_kickoff)} — {m.home_name} vs {m.away_name}
                  </option>
                ))}
              </select>
            )
          )}
        </Field>

        {audience === 'marketplace' && !includeMatch && (
          <Field label="Expiry">
            <div className="grid grid-cols-6 gap-1.5">
              {(['1h', '6h', '24h', '7d', 'custom', 'never'] as ExpirySlot[]).map((s) => (
                <button
                  key={s} type="button" onClick={() => setExpiry(s)}
                  className={`py-1.5 text-xs rounded-md ${expiry === s ? 'bg-pitch-500/20 text-pitch-400 ring-1 ring-pitch-500/40' : 'bg-ink-800 text-chalk'}`}
                >{s === 'never' ? '∞' : s === 'custom' ? '📅' : s}</button>
              ))}
            </div>
            {expiry === 'custom' && (
              <input
                type="datetime-local"
                value={customExpiry} onChange={(e) => setCustomExpiry(e.target.value)}
                min={new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 16)}
                className="mt-2 w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk font-mono"
              />
            )}
            {expiry === 'never' && (
              <p className="text-[10px] text-amber-400 mt-1.5">⚠ This bet stays open forever. Cancel it manually if no one claims.</p>
            )}
          </Field>
        )}

        <Field label="Odds">
          <div className="flex gap-2 mb-2">
            <RadioBtn label="Even (1:1)" active={oddsMode === 'even'} onClick={() => setOddsMode('even')} />
            <RadioBtn label="Variable" active={oddsMode === 'variable'} onClick={() => setOddsMode('variable')} />
          </div>
          {oddsMode === 'even' ? (
            <>
              <div className="grid grid-cols-5 gap-1.5">
                {STAKE_PRESETS.map((v) => (
                  <button
                    key={v} type="button" onClick={() => setEvenStake(v)}
                    className={`py-1.5 text-sm rounded-md ${evenStake === v ? 'bg-pitch-500/20 text-pitch-400 ring-1 ring-pitch-500/40' : 'bg-ink-800 text-chalk'}`}
                  >€{v}</button>
                ))}
              </div>
              <input
                type="number" min={1} max={10000} value={evenStake}
                onChange={(e) => setEvenStake(Math.max(1, Math.min(10000, Number(e.target.value) || 1)))}
                className="mt-2 w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk font-mono"
              />
            </>
          ) : (
            <div className="space-y-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-dim mb-1">You risk (€)</div>
                <input
                  type="number" min={1} max={10000} value={proposerStake}
                  onChange={(e) => setProposerStake(Math.max(1, Math.min(10000, Number(e.target.value) || 1)))}
                  className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk font-mono"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-dim mb-1">{opponent || 'Opponent'} risks (€)</div>
                <input
                  type="number" min={1} max={10000} value={opponentStake}
                  onChange={(e) => setOpponentStake(Math.max(1, Math.min(10000, Number(e.target.value) || 1)))}
                  className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk font-mono"
                />
              </div>
            </div>
          )}
          <PayoutPreview stakes={stakes} ratio={oddsRatio} opponent={opponent || 'Opponent'} />
        </Field>

        <Field label="Trash talk (optional)">
          <div className="flex gap-2">
            <input
              type="text" value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder=""
              className="flex-1 bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk"
            />
            <button
              type="button"
              disabled={!description.trim() || !opponent}
              onClick={async () => {
                try {
                  const r = await aiBetTrashTalk({ description: description.trim(), stake: stakes.proposer, opponent });
                  if (r.text) { setMessage(r.text); toast.success('AI line drafted.'); haptic.tap(); }
                  else toast.error('No suggestion.');
                } catch (e) { toast.error((e as Error).message); }
              }}
              className="h-[2.5rem] px-2.5 rounded-lg bg-cyan-500/15 text-cyan-400 text-[10px] font-semibold ring-1 ring-cyan-500/30 disabled:opacity-40"
              title="Generate AI trash talk"
            >AI 🎤</button>
          </div>
        </Field>

        {error && <div className="text-xs text-red-400">{error}</div>}

        <button
          type="submit" disabled={mut.isPending}
          className="w-full h-11 rounded-lg bg-pitch-500 text-ink-950 font-semibold text-sm hover:bg-pitch-400 active:bg-pitch-600 disabled:opacity-50"
        >
          {mut.isPending
            ? 'Sending…'
            : audience === 'marketplace'
              ? 'Post to marketplace'
              : `Send to ${opponent || '…'}`}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-dim block mb-1">{label}</span>
      {children}
    </label>
  );
}

function RadioBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button" onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-xs font-medium ${active ? 'bg-pitch-500/20 text-pitch-400 ring-1 ring-pitch-500/40' : 'bg-ink-800 text-chalk'}`}
    >{label}</button>
  );
}

function SideBtn({ label, sub, active, onClick }: { label: string; sub: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button" onClick={onClick}
      className={`px-3 py-2 rounded-md text-left transition-colors ${active ? 'bg-pitch-500/20 ring-1 ring-pitch-500/40' : 'bg-ink-800 hover:bg-ink-700'}`}
    >
      <div className={`text-sm font-semibold ${active ? 'text-pitch-400' : 'text-chalk'}`}>{label}</div>
      <div className="text-[10px] text-dim">{sub}</div>
    </button>
  );
}

function PayoutPreview({ stakes, ratio, opponent }: { stakes: { proposer: number; opponent: number }; ratio: number; opponent: string }) {
  const ratioLabel = stakes.proposer === stakes.opponent
    ? '1 : 1'
    : ratio >= 1
      ? `${ratio.toFixed(2)} : 1`
      : `1 : ${(1 / ratio).toFixed(2)}`;
  return (
    <div className="mt-2 rounded-lg bg-ink-800/60 p-2.5 space-y-1.5">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-dim">
        <span>If you win</span>
        <span className="text-pitch-400 font-mono normal-case tracking-normal">+€{stakes.opponent}</span>
      </div>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-dim">
        <span>If {opponent} wins</span>
        <span className="text-red-400 font-mono normal-case tracking-normal">-€{stakes.proposer}</span>
      </div>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-dim pt-1 border-t border-ink-700">
        <span>Odds</span>
        <span className="font-mono normal-case tracking-normal text-chalk">{ratioLabel}</span>
      </div>
    </div>
  );
}

function expiryLabel(s: ExpirySlot): string {
  return { '1h': '1 hour', '6h': '6 hours', '24h': '1 day', '7d': '7 days', custom: 'custom', never: 'never' }[s];
}

function MatchPreview({ m }: { m: EnrichedMatch }) {
  return (
    <div className="rounded-lg bg-ink-800 px-3 py-2 text-sm">
      <div className="text-xs text-dim mb-1">{rigaDate(m.utc_kickoff)} · {rigaTime(m.utc_kickoff)}</div>
      <div>{m.home_flag ?? '🏳️'} {m.home_name} vs {m.away_flag ?? '🏳️'} {m.away_name}</div>
    </div>
  );
}
